import { Inject, Injectable, InjectionToken } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, catchError, of, switchMap, timer } from 'rxjs';
import { HAS_SESSION, configuredPaths } from './lazy-feature-route';

/** The most visited entity pages: fetched first, as soon as the browser is idle after startup. */
export const PRIORITY_PRELOAD_PATHS = ['items', 'challenges', 'releases'];
/** Everything else waits a bit longer so it doesn't compete with the first screen's API calls. */
const PRIORITY_DELAY_MS = 0;
const REST_DELAY_MS = 5_000;

/** Resolves once `delayMs` has passed and the browser is idle — swapped out in tests. */
export const PRELOAD_WHEN_IDLE = new InjectionToken<(delayMs: number) => Observable<unknown>>('PRELOAD_WHEN_IDLE', {
  providedIn: 'root',
  factory: () => (delayMs: number) =>
    timer(delayMs).pipe(
      switchMap(
        () =>
          new Observable<void>((subscriber) => {
            const done = () => {
              subscriber.next();
              subscriber.complete();
            };
            if (typeof requestIdleCallback !== 'function') {
              const handle = setTimeout(done, 200);
              return () => clearTimeout(handle);
            }
            // The timeout caps the wait when the page never goes idle (e.g. a busy AG Grid).
            const handle = requestIdleCallback(done, { timeout: 10_000 });
            return () => cancelIdleCallback(handle);
          }),
      ),
    ),
});

/**
 * Preloads the lazy entity features in the background so the first visit to a page doesn't wait on
 * the network, while keeping them out of the initial bundle.
 *
 * Unlike PreloadAllModules it waits for the browser to be idle, fetches the most used features
 * first, and skips preloading on Save-Data connections and while signed out (the router's preloader
 * ignores canMatch, so this is what keeps the login screen from downloading every chunk). The
 * router re-runs preloading on each NavigationEnd, so it starts right after sign-in.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloadingStrategy implements PreloadingStrategy {
  constructor(
    @Inject(PRELOAD_WHEN_IDLE) private whenIdle: (delayMs: number) => Observable<unknown>,
    @Inject(HAS_SESSION) private hasSession: () => boolean,
  ) {}

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if ((navigator as any).connection?.saveData || !this.hasSession()) return of(null);

    const paths = configuredPaths([route]);
    const priority = paths.some((p) => PRIORITY_PRELOAD_PATHS.includes(p));
    return this.whenIdle(priority ? PRIORITY_DELAY_MS : REST_DELAY_MS).pipe(
      switchMap(() => load()),
      // Same as PreloadAllModules: a failed preload (e.g. a chunk gone after a deploy) must not stop
      // the other preloads. The real navigation retries the load and StaleChunkRecoveryService
      // handles it from there.
      catchError(() => of(null)),
    );
  }
}
